import type { CategoryContent } from '../row.js';

/** Geography & Earth Systems, second chunk. Correct option first; rotated at build. */
export const MORE_GEOGRAPHY: CategoryContent = {
  msc: [
    [
      'What does the Bowen ratio express?',
      [
        'The ratio of sensible to latent heat flux from a surface',
        'The ratio of net radiation to ground heat flux',
        'The ratio of actual to potential evapotranspiration',
        'The ratio of albedo to surface emissivity',
      ],
      0,
      'High over a desert, low over open water. It is the first thing you look at when partitioning a surface energy balance.',
    ],
    [
      'A rain shadow forms because of what?',
      [
        'Forced ascent removes moisture on the windward side',
        'Higher surface albedo on the leeward slope suppresses convection',
        'Cold air drainage displaces moist air from the leeward valley',
        'Reduced atmospheric pressure at altitude prevents condensation',
      ],
      0,
      'The Atacama, the Patagonian steppe and the Great Basin are all the same mechanism on different ranges.',
    ],
    [
      'What does the Koppen classification use as its primary criteria?',
      [
        'Monthly temperature and precipitation thresholds',
        'Net radiation balance and dominant air-mass source regions',
        'Potential evapotranspiration relative to soil moisture storage',
        'Latitude bands adjusted for continentality and elevation',
      ],
      0,
      'The vegetation calibration is why the boundaries sometimes look arbitrary in climate terms - they were drawn to match biomes, not isotherms.',
    ],
    [
      'Thermohaline circulation is driven by what?',
      [
        'Density differences arising from temperature and salinity contrasts',
        'Wind stress on the ocean surface across the trade wind belts',
        'Tidal mixing over mid-ocean ridges and continental shelves',
        'The Coriolis deflection of western boundary currents',
      ],
      0,
      'Deep water forms where surface water is cold and salty enough to sink - the Labrador and Greenland seas, and around Antarctica.',
    ],
    [
      'What is a karst landscape formed by?',
      [
        'Dissolution of soluble rock, principally limestone',
        'Frost shattering of jointed rock in periglacial conditions',
        'Deflation and abrasion of bedrock by wind-transported sand',
        'Differential compaction of unconsolidated sediment over bedrock highs',
      ],
      0,
      'Sinkholes, caves and disappearing streams. It is also why karst aquifers are so vulnerable to contamination - almost no filtration.',
    ],
    [
      'The Hadley cell descends at roughly which latitude?',
      ['30 degrees', '10 degrees', '50 degrees', '60 degrees'],
      0,
      'Which is why the great subtropical deserts sit in a band at about that latitude in both hemispheres.',
    ],
    [
      'What distinguishes a delta from an estuary?',
      [
        'A delta forms where sediment supply outpaces removal; an estuary is a drowned river mouth',
        'A delta is tidal and an estuary is not',
        'A delta occurs on passive margins and an estuary on active ones',
        'A delta is freshwater throughout and an estuary is saline throughout',
      ],
      0,
      'Both can be tidal. The Nile builds out; the Thames is flooded - a difference in the sediment budget, not in the water.',
    ],
    [
      'Isostasy refers to what?',
      [
        'The buoyant equilibrium of the crust floating on a denser, deformable mantle',
        'The balance between erosion and uplift in a mountain range',
        'The equilibrium profile a river adopts between source and mouth',
        'The steady state between sea-floor spreading and subduction',
      ],
      0,
      'Which is why thick crust stands high, why ice sheets depress the land beneath them, and why the Baltic is still rising.',
    ],
    [
      'What is the significance of the 4.2 ka event?',
      [
        'An abrupt aridification episode implicated in the disruption of several Bronze Age societies',
        'The final drainage of the North American proglacial lakes',
        'The onset of the current interglacial in the northern hemisphere',
        'The peak of Holocene sea-level rise in the Mediterranean, recorded in submerged shoreline deposits',
      ],
      0,
      'It is the boundary of the Meghalayan stage. Whether it caused the collapses attributed to it, or merely coincided with them, is argued hard.',
    ],
    [
      'Loess is best described as what?',
      [
        'Wind-deposited silt, typically of glacial or desert-margin origin',
        'Glacially transported unsorted debris left at an ice margin',
        'Fine-grained lake sediment laid down in annual layers',
        'Weathered volcanic ash altered to clay in situ',
      ],
      0,
      'The North China, Mississippi and Ukrainian loess belts are among the most productive soils in existence and among the most erodible.',
    ],
  ],
  phd: [
    [
      'What does the Rossby radius of deformation set the scale of?',
      [
        'The length over which rotation begins to dominate buoyancy in a stratified fluid',
        'The wavelength of a planetary wave in the mid-latitude westerlies',
        'The depth of the oceanic mixed layer under steady wind forcing',
        'The horizontal extent of a tropical cyclone eyewall',
      ],
      0,
      'It is why ocean eddies are tens of kilometres across and atmospheric ones are hundreds - the stratification and the scale height differ.',
    ],
    [
      'Ekman transport in the northern hemisphere moves surface water in which direction?',
      [
        'Ninety degrees to the right of the wind',
        'Directly downwind, decaying exponentially with depth',
        'Ninety degrees to the left of the wind',
        'Along isobars, in geostrophic balance with the pressure field',
      ],
      0,
      'Which is what makes coastal upwelling work: an alongshore wind drives surface water offshore and cold water replaces it.',
    ],
    [
      'What does the Budyko framework relate?',
      [
        'The partition of precipitation between evaporation and runoff, as a function of aridity',
        'Planetary albedo to global mean surface temperature',
        'Soil carbon turnover to mean annual temperature, through an exponential temperature sensitivity',
        'Sediment yield to basin relief and precipitation',
      ],
      0,
      'A remarkably robust one-parameter curve, which is why deviations from it are treated as diagnostic of land-use or climate change.',
    ],
    [
      'The concept of the critical zone in earth-surface science refers to what?',
      [
        'The near-surface layer from vegetation canopy to groundwater where rock, water, air and life interact',
        'The zone of maximum shear stress at the base of a glacier',
        'The depth interval over which soil carbon is most vulnerable to warming',
        'The band of latitudes most sensitive to monsoon failure, where interannual rainfall variability is greatest',
      ],
      0,
      'A deliberately integrative framing, and one that got substantial observatory funding on the argument that the disciplines were failing to talk.',
    ],
    [
      'What is a knickpoint, and why does it matter?',
      [
        'A break in a river\'s longitudinal profile that migrates upstream, recording changes in base level or uplift',
        'The point at which a channel changes from bedrock to alluvial control',
        'The confluence at which two streams of equal order meet, incrementing the order of the channel downstream',
        'The elevation at which a glacier\'s accumulation and ablation balance',
      ],
      0,
      'Knickpoint celerity is how tectonic signals are read out of topography, and the stream-power model is what converts one into the other.',
    ],
    [
      'The Younger Dryas is most commonly explained by what?',
      [
        'A meltwater pulse disrupting North Atlantic deep-water formation',
        'A sequence of large tropical volcanic eruptions',
        'An orbital minimum in northern-hemisphere summer insolation',
        'A reduction in solar output coincident with a grand minimum',
      ],
      0,
      'The routing of Lake Agassiz outflow is the classic mechanism; the impact hypothesis has been repeatedly proposed and repeatedly contested.',
    ],
    [
      'What does the term "teleconnection" mean in climatology?',
      [
        'A statistically robust link between climate anomalies in widely separated regions',
        'The transfer of heat between hemispheres by the atmospheric circulation',
        'The propagation of a tsunami across an entire ocean basin',
        'The coupling of stratospheric and tropospheric circulation over a pole',
      ],
      0,
      'ENSO to the North American winter is the canonical example; the North Atlantic Oscillation is another.',
    ],
    [
      'Cosmogenic nuclide dating of a landform measures what?',
      [
        'Accumulated exposure to cosmic rays at or near the surface',
        'The time since the last thermal resetting of a mineral\'s luminescence signal',
        'The decay of potassium to argon within a volcanic mineral',
        'The residence time of water in a groundwater body',
      ],
      0,
      'Beryllium-10 in quartz being the workhorse. It is what allowed glacial chronologies to be built on boulders rather than on organic material.',
    ],
    [
      'What is the principal difficulty in attributing a single extreme weather event to climate change?',
      [
        'Attribution is probabilistic',
        'Observational records are too short to establish any baseline at all',
        'Climate models cannot resolve synoptic-scale systems',
        'Natural variability and forced change cannot be separated even in principle',
      ],
      0,
      'Which is why the literature reports fraction of attributable risk rather than yes-or-no answers, and why the framing keeps being misreported.',
    ],
    [
      'The World Reference Base and USDA soil taxonomies differ chiefly in what respect?',
      [
        'The reference base classifies on observable diagnostic horizons; the USDA system leans on inferred genesis and climate',
        'One applies only to agricultural soils and the other to all land surfaces',
        'One is based on texture alone and the other on chemistry alone',
        'One is used only in Europe and has no international standing, which is why global datasets adopt the other by default',
      ],
      0,
      'It is why the same profile can be a Gleysol and an Aquept, and why cross-continental soil datasets need a translation step.',
    ],
  ],
  professor: [
    [
      'What does the stream-power incision model predict, and what is its main weakness?',
      [
        'Erosion rate as a power function of drainage area and slope',
        'Sediment flux as a linear function of channel width, valid only in bedrock channels',
        'Hillslope erosion as a diffusive process, failing where landslides dominate',
        'Basin-averaged denudation from relief alone, failing in tectonically quiet settings',
      ],
      0,
      'It is the standard workhorse and its exponents are effectively fitted rather than derived, which makes inverting topography for uplift history contentious.',
    ],
    [
      'The "tectonic aneurysm" hypothesis proposes what?',
      [
        'Focused fluvial erosion can localise rapid rock uplift and metamorphism by unloading the crust',
        'Slab break-off drives sudden surface uplift across an entire orogen',
        'Glacial buzzsaw action limits mountain height regardless of tectonic rate',
        'Delamination of the lower crust causes plateau collapse at the margins',
      ],
      0,
      'Nanga Parbat and Namche Barwa are the type localities - erosion as a tectonic driver rather than merely a response.',
    ],
    [
      'What is the Zebiak-Cane model notable for?',
      [
        'Being the first coupled model to forecast El Nino from ocean-atmosphere dynamics',
        'Providing the first eddy-resolving simulation of the Southern Ocean',
        'Establishing the modern parameterisation of atmospheric convection',
        'Reconstructing Pliocene sea surface temperatures from proxies',
      ],
      0,
      '1987, and intermediate-complexity by design. It made ENSO a predictability problem rather than a statistical curiosity.',
    ],
    [
      'What does the concept of "geodiversity" contribute, and why is it contested?',
      [
        'It extends conservation reasoning to abiotic features',
        'It measures the variety of soil types within a landscape unit',
        'It quantifies the range of erosion processes acting on a slope',
        'It indexes lithological variation for use in hazard mapping',
      ],
      0,
      'The practical difficulty is that biodiversity has counting units and geodiversity does not, which makes trade-offs in planning hard to formalise.',
    ],
    [
      'Bond events are identified from what evidence, and how secure are they?',
      [
        'North Atlantic ice-rafted debris in Holocene marine cores',
        'Antarctic ice-core dust flux, securely dated by annual layer counting',
        'Speleothem oxygen isotopes from monsoon regions, well replicated globally',
        'Tree-ring width chronologies from bristlecone pine, precisely annual',
      ],
      0,
      'Bond\'s roughly 1,500-year spacing has been questioned on both the statistics and the dating, and remains the standard citation regardless.',
    ],
    [
      'What does the "sediment routing system" framing change about provenance analysis?',
      [
        'It treats source-to-sink transfer as a signal-processing problem',
        'It restricts provenance interpretation to single-mineral geochemistry',
        'It assumes instantaneous transfer from erosion to deposition',
        'It applies only to marine deltaic systems',
      ],
      0,
      'The consequence is uncomfortable: a stratigraphic record may not preserve the climate signal that produced it, if the system buffers longer than the forcing.',
    ],
    [
      'The Antarctic Circumpolar Current\'s role in the Cenozoic is debated how?',
      [
        'Whether its onset caused Antarctic glaciation by thermal isolation, or followed CO2-driven cooling',
        'Whether it existed before the Miocene at all, given the ambiguity of the early Southern Ocean record',
        'Whether it transports more heat than the Gulf Stream',
        'Whether it is wind-driven or thermohaline in origin',
      ],
      0,
      'The gateway hypothesis versus the greenhouse-gas hypothesis. Proxy CO2 reconstructions have moved the argument substantially toward the latter.',
    ],
    [
      'What is "hydrological non-stationarity" and why is it consequential?',
      [
        'The premise that historical flow statistics no longer describe future distributions, invalidating standard design methods',
        'The tendency of river gauges to drift out of calibration over decades',
        'The seasonal variation of baseflow in snowmelt-dominated basins',
        'The dependence of flood frequency on antecedent soil moisture, which makes any single-variable frequency analysis inadequate',
      ],
      0,
      'Milly et al., "Stationarity is dead". Every hundred-year flood estimate in a design code rests on the assumption it rejects.',
    ],
    [
      'What does the "sensitivity versus resilience" distinction do for geomorphic systems analysis?',
      [
        'It separates the magnitude of a system\'s response from its capacity to return to a prior state',
        'It distinguishes measurement error from genuine landscape change',
        'It separates tectonic from climatic forcing in an erosion signal',
        'It distinguishes bedrock from alluvial channel behaviour, which respond to the same forcing differently',
      ],
      0,
      'It is what lets you say a landscape is highly responsive and also stable, which sounds contradictory until the two axes are separated.',
    ],
    [
      'Why is the concept of the Anthropocene contested as a formal geological unit?',
      [
        'Its stratigraphic marker and start date are disputed',
        'There is no measurable human signal in the sedimentary record',
        'It overlaps entirely with the Holocene as previously defined',
        'It cannot be identified outside terrestrial sequences',
      ],
      0,
      'The 2024 vote against the proposed epoch did not settle the concept\'s usefulness - only its formal status, which is a distinction the reporting largely lost.',
    ],
  ],
};
